/**
 * Super Admin — Painel de gerenciamento de Tenants (White Label)
 * Dashboard + CRUD completo de tenants
 */

import { useState, useEffect, useCallback } from 'preact/hooks';
import { T } from '../styles/tokens';
import { api } from '../api/client';
import { showToast } from '../components/Toast';

// ── Tipos ──────────────────────────────────────────────────────────────

interface Tenant {
  id: string;
  slug: string;
  domain: string | null;
  name: string;
  logo_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  accent_color: string;
  bg_color: string;
  email_from: string | null;
  support_email: string | null;
  stripe_account_id: string | null;
  plan: string;
  features_json: string;
  custom_css: string | null;
  max_customers: number;
  max_agents: number;
  is_active: number;
  created_at: string;
  updated_at: string;
  customer_count?: number;
}

interface Stats {
  total_tenants: number;
  total_customers: number;
  active_tenants: number;
  mrr: number;
}

// ── Estilos ────────────────────────────────────────────────────────────

const S = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' } as any,
  title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 8 } as any,
  subtitle: { fontSize: 14, color: T.textSecondary, marginBottom: 24 } as any,
  tabs: { display: 'flex', gap: 0, borderBottom: `1px solid ${T.border}`, marginBottom: 24 } as any,
  tab: (active: boolean) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: active ? 600 : 400,
    color: active ? T.primary : T.textSecondary, borderBottom: active ? `2px solid ${T.primary}` : '2px solid transparent',
    background: 'none', border: 'none', transition: 'all 0.2s',
  }) as any,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 } as any,
  card: {
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: '20px',
  } as any,
  cardLabel: { fontSize: 12, color: T.textMuted, textTransform: 'uppercase' as any, marginBottom: 4 } as any,
  cardValue: { fontSize: 28, fontWeight: 700, color: T.text } as any,
  table: { width: '100%', borderCollapse: 'collapse' as any } as any,
  th: {
    textAlign: 'left' as any, padding: '12px 16px', fontSize: 12, fontWeight: 600,
    color: T.textMuted, textTransform: 'uppercase' as any, borderBottom: `1px solid ${T.border}`,
  } as any,
  td: { padding: '12px 16px', fontSize: 14, color: T.text, borderBottom: `1px solid ${T.border}` } as any,
  badge: (active: boolean) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
    background: active ? T.successBgSubtle : T.warningBgSubtle,
    color: active ? T.success : T.warning,
  }) as any,
  btn: (variant: 'primary' | 'danger' | 'ghost') => ({
    padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
    border: variant === 'ghost' ? `1px solid ${T.border}` : 'none',
    background: variant === 'primary' ? T.primary : variant === 'danger' ? T.danger : 'transparent',
    color: variant === 'primary' ? '#fff' : variant === 'danger' ? '#fff' : T.textSecondary,
    transition: 'all 0.2s',
  }) as any,
  formGroup: { marginBottom: 16 } as any,
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: T.textSecondary, marginBottom: 4 } as any,
  input: {
    width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${T.border}`,
    background: T.bgInput, color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as any,
  } as any,
  select: {
    width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${T.border}`,
    background: T.bgInput, color: T.text, fontSize: 14, outline: 'none',
  } as any,
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 } as any,
  modal: {
    position: 'fixed' as any, inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  } as any,
  modalContent: {
    background: T.bgCard, borderRadius: 16, padding: 32, width: '90%', maxWidth: 600,
    maxHeight: '85vh', overflowY: 'auto' as any, border: `1px solid ${T.border}`,
  } as any,
};

// ── Componente Principal ───────────────────────────────────────────────

export function SuperAdminPage() {
  const [tab, setTab] = useState<'dashboard' | 'tenants' | 'create'>('dashboard');
  const [stats, setStats] = useState<Stats | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [editTenant, setEditTenant] = useState<Tenant | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get<Stats>('/super-admin/stats'),
        api.get<{ tenants: Tenant[] }>('/super-admin/tenants'),
      ]);
      setStats(s);
      setTenants(t.tenants);
    } catch (e: any) {
      showToast(e?.message || 'Erro ao carregar dados', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, []);

  return (
    <div style={S.page}>
      <h1 style={S.title}>🏢 Super Admin</h1>
      <p style={S.subtitle}>Gerenciamento de tenants White Label</p>

      <div style={S.tabs}>
        <button style={S.tab(tab === 'dashboard')} onClick={() => setTab('dashboard')}>Dashboard</button>
        <button style={S.tab(tab === 'tenants')} onClick={() => setTab('tenants')}>Tenants</button>
        <button style={S.tab(tab === 'create')} onClick={() => setTab('create')}>+ Criar Tenant</button>
      </div>

      {loading ? (
        <p style={{ color: T.textMuted, textAlign: 'center', padding: 40 }}>Carregando...</p>
      ) : (
        <>
          {tab === 'dashboard' && <DashboardTab stats={stats} />}
          {tab === 'tenants' && (
            <TenantsTab tenants={tenants} onRefresh={fetchData} onEdit={setEditTenant} />
          )}
          {tab === 'create' && <CreateTenantForm onCreated={() => { fetchData(); setTab('tenants'); }} />}
        </>
      )}

      {editTenant && (
        <EditTenantModal tenant={editTenant} onClose={() => setEditTenant(null)} onSaved={() => { setEditTenant(null); fetchData(); }} />
      )}
    </div>
  );
}

// ── Dashboard Tab ──────────────────────────────────────────────────────

function DashboardTab({ stats }: { stats: Stats | null }) {
  if (!stats) return null;
  const cards = [
    { label: 'Total Tenants', value: stats.total_tenants, icon: '🏢' },
    { label: 'Tenants Ativos', value: stats.active_tenants, icon: '✅' },
    { label: 'Total Customers', value: stats.total_customers, icon: '👥' },
    { label: 'MRR (placeholder)', value: `R$ ${stats.mrr}`, icon: '💰' },
  ];
  return (
    <div style={S.grid}>
      {cards.map((c) => (
        <div key={c.label} style={S.card}>
          <div style={S.cardLabel}>{c.icon} {c.label}</div>
          <div style={S.cardValue}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ── Tenants Tab ────────────────────────────────────────────────────────

function TenantsTab({ tenants, onRefresh, onEdit }: { tenants: Tenant[]; onRefresh: () => void; onEdit: (t: Tenant) => void }) {
  const toggleActive = async (t: Tenant) => {
    try {
      if (t.is_active) {
        await api.delete(`/super-admin/tenants/${t.id}`);
        showToast(`Tenant "${t.name}" desativado`, 'success');
      } else {
        await api.post(`/super-admin/tenants/${t.id}/activate`);
        showToast(`Tenant "${t.name}" reativado`, 'success');
      }
      onRefresh();
    } catch (e: any) {
      showToast(e?.message || 'Erro', 'error');
    }
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Nome</th>
            <th style={S.th}>Slug</th>
            <th style={S.th}>Domínio</th>
            <th style={S.th}>Plano</th>
            <th style={S.th}>Customers</th>
            <th style={S.th}>Status</th>
            <th style={S.th}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr key={t.id}>
              <td style={S.td}>{t.name}</td>
              <td style={S.td}><code style={{ fontSize: 12 }}>{t.slug}</code></td>
              <td style={S.td}>{t.domain || '—'}</td>
              <td style={S.td}>{t.plan}</td>
              <td style={S.td}>{t.customer_count ?? 0}</td>
              <td style={S.td}><span style={S.badge(!!t.is_active)}>{t.is_active ? 'Ativo' : 'Inativo'}</span></td>
              <td style={{ ...S.td, display: 'flex', gap: 8 }}>
                <button style={S.btn('ghost')} onClick={() => onEdit(t)}>✏️</button>
                <button style={S.btn(t.is_active ? 'danger' : 'primary')} onClick={() => toggleActive(t)}>
                  {t.is_active ? '⏸' : '▶'}
                </button>
              </td>
            </tr>
          ))}
          {tenants.length === 0 && (
            <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: T.textMuted }}>Nenhum tenant encontrado</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Create Tenant Form ─────────────────────────────────────────────────

function CreateTenantForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({
    name: '', slug: '', domain: '', logo_url: '', plan: 'starter',
    primary_color: '#3B82F6', accent_color: '#8B5CF6', bg_color: '#0A0A0A',
    max_customers: 50, max_agents: 5,
  });
  const [saving, setSaving] = useState(false);

  const set = (key: string, value: any) => setForm({ ...form, [key]: value });

  // Auto-gerar slug do nome
  const handleNameChange = (name: string) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setForm({ ...form, name, slug });
  };

  const submit = async () => {
    if (!form.name || !form.slug) { showToast('Nome e slug são obrigatórios', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/super-admin/tenants', form);
      showToast(`Tenant "${form.name}" criado com sucesso!`, 'success');
      onCreated();
    } catch (e: any) {
      showToast(e?.message || 'Erro ao criar tenant', 'error');
    }
    setSaving(false);
  };

  return (
    <div style={{ ...S.card, maxWidth: 600 }}>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: T.text, marginBottom: 20 }}>Novo Tenant</h3>

      <div style={S.formRow}>
        <div style={S.formGroup}>
          <label style={S.label}>Nome *</label>
          <input style={S.input} value={form.name} onInput={(e: any) => handleNameChange(e.target.value)} placeholder="Ex: JetClaw" />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Slug *</label>
          <input style={S.input} value={form.slug} onInput={(e: any) => set('slug', e.target.value)} placeholder="ex: jetclaw" />
        </div>
      </div>

      <div style={S.formGroup}>
        <label style={S.label}>Domínio</label>
        <input style={S.input} value={form.domain} onInput={(e: any) => set('domain', e.target.value)} placeholder="ex: app.jetclaw.com" />
      </div>

      <div style={S.formGroup}>
        <label style={S.label}>Logo URL</label>
        <input style={S.input} value={form.logo_url} onInput={(e: any) => set('logo_url', e.target.value)} placeholder="https://..." />
      </div>

      <div style={S.formRow}>
        <div style={S.formGroup}>
          <label style={S.label}>Plano</label>
          <select style={S.select} value={form.plan} onChange={(e: any) => set('plan', e.target.value)}>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Máx. Customers</label>
          <input style={S.input} type="number" value={form.max_customers} onInput={(e: any) => set('max_customers', +e.target.value)} />
        </div>
      </div>

      <div style={S.formRow}>
        <div style={S.formGroup}>
          <label style={S.label}>Máx. Agentes</label>
          <input style={S.input} type="number" value={form.max_agents} onInput={(e: any) => set('max_agents', +e.target.value)} />
        </div>
        <div style={S.formGroup} />
      </div>

      <div style={{ ...S.formRow, gridTemplateColumns: '1fr 1fr 1fr' }}>
        <div style={S.formGroup}>
          <label style={S.label}>Cor Primária</label>
          <input type="color" value={form.primary_color} onInput={(e: any) => set('primary_color', e.target.value)} style={{ width: '100%', height: 36, border: 'none', cursor: 'pointer' }} />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Cor Accent</label>
          <input type="color" value={form.accent_color} onInput={(e: any) => set('accent_color', e.target.value)} style={{ width: '100%', height: 36, border: 'none', cursor: 'pointer' }} />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Cor Background</label>
          <input type="color" value={form.bg_color} onInput={(e: any) => set('bg_color', e.target.value)} style={{ width: '100%', height: 36, border: 'none', cursor: 'pointer' }} />
        </div>
      </div>

      <button style={{ ...S.btn('primary'), width: '100%', padding: '12px', fontSize: 15, marginTop: 8 }} onClick={submit} disabled={saving}>
        {saving ? 'Criando...' : '🏢 Criar Tenant'}
      </button>
    </div>
  );
}

// ── Edit Tenant Modal ──────────────────────────────────────────────────

function EditTenantModal({ tenant, onClose, onSaved }: { tenant: Tenant; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: tenant.name, domain: tenant.domain || '', logo_url: tenant.logo_url || '',
    plan: tenant.plan, primary_color: tenant.primary_color || '#3B82F6',
    accent_color: tenant.accent_color || '#8B5CF6', bg_color: tenant.bg_color || '#0A0A0A',
    max_customers: tenant.max_customers, max_agents: tenant.max_agents,
    support_email: tenant.support_email || '', email_from: tenant.email_from || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (key: string, value: any) => setForm({ ...form, [key]: value });

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/super-admin/tenants/${tenant.id}`, form);
      showToast('Tenant atualizado!', 'success');
      onSaved();
    } catch (e: any) {
      showToast(e?.message || 'Erro ao salvar', 'error');
    }
    setSaving(false);
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={S.modalContent} onClick={(e: any) => e.stopPropagation()}>
        <h3 style={{ fontSize: 18, fontWeight: 600, color: T.text, marginBottom: 20 }}>
          Editar: {tenant.name} <code style={{ fontSize: 12, color: T.textMuted }}>({tenant.slug})</code>
        </h3>

        <div style={S.formRow}>
          <div style={S.formGroup}>
            <label style={S.label}>Nome</label>
            <input style={S.input} value={form.name} onInput={(e: any) => set('name', e.target.value)} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Domínio</label>
            <input style={S.input} value={form.domain} onInput={(e: any) => set('domain', e.target.value)} />
          </div>
        </div>

        <div style={S.formGroup}>
          <label style={S.label}>Logo URL</label>
          <input style={S.input} value={form.logo_url} onInput={(e: any) => set('logo_url', e.target.value)} />
        </div>

        <div style={S.formRow}>
          <div style={S.formGroup}>
            <label style={S.label}>Email From</label>
            <input style={S.input} value={form.email_from} onInput={(e: any) => set('email_from', e.target.value)} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Email Suporte</label>
            <input style={S.input} value={form.support_email} onInput={(e: any) => set('support_email', e.target.value)} />
          </div>
        </div>

        <div style={S.formRow}>
          <div style={S.formGroup}>
            <label style={S.label}>Plano</label>
            <select style={S.select} value={form.plan} onChange={(e: any) => set('plan', e.target.value)}>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Máx. Customers</label>
            <input style={S.input} type="number" value={form.max_customers} onInput={(e: any) => set('max_customers', +e.target.value)} />
          </div>
        </div>

        <div style={{ ...S.formRow, gridTemplateColumns: '1fr 1fr 1fr' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Cor Primária</label>
            <input type="color" value={form.primary_color} onInput={(e: any) => set('primary_color', e.target.value)} style={{ width: '100%', height: 36, border: 'none', cursor: 'pointer' }} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Cor Accent</label>
            <input type="color" value={form.accent_color} onInput={(e: any) => set('accent_color', e.target.value)} style={{ width: '100%', height: 36, border: 'none', cursor: 'pointer' }} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Cor Background</label>
            <input type="color" value={form.bg_color} onInput={(e: any) => set('bg_color', e.target.value)} style={{ width: '100%', height: 36, border: 'none', cursor: 'pointer' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button style={{ ...S.btn('ghost'), flex: 1 }} onClick={onClose}>Cancelar</button>
          <button style={{ ...S.btn('primary'), flex: 1 }} onClick={submit} disabled={saving}>
            {saving ? 'Salvando...' : '💾 Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
